import React, { useState, useCallback } from 'react';
import { Row, Col, FormGroup, Input, InputGroup, InputGroupAddon, Button } from 'reactstrap';
import { getLandlordByMailEndpoint } from '../../../services/landlordService';
import useAdminEntity from '../../../hooks/useAdminQuotationEntity';
import { ClientInfo, ClientInfoSectionProps, ClientSearchProps } from '../interfaces/client.interface';
import { LandlordResponse } from '../interfaces/api-responses.interface';

// ============ SHARED COMPONENTS ============
interface FieldProps {
  label: string;
  type?: string;
  placeholder: string;
  value: string | number;
  onChange: (value: string) => void;
  readOnly?: boolean;
  colSize?: string;
  symbolData?: string;
}

const Field: React.FC<FieldProps> = ({ 
  label, 
  type = 'text', 
  placeholder, 
  value, 
  onChange, 
  readOnly = false,
  colSize = '4',
  symbolData
}) => {
  const displayValue = (() => {
    if (type === 'number' && (value === 0 || value === '0')) {
      return '';
    }
    return value;
  })();
  
  // Determinar si el input tiene contenido (excluir ceros para números)
  const hasContent = (() => {
    if (type === 'number') {
      return value !== '' && value !== 0 && value !== '0' && value !== null && value !== undefined;
    }
    return value !== '' && value !== null && value !== undefined;
  })();
  
  // Estilos dinámicos para el input
  const inputStyles = {
    ...(readOnly ? { backgroundColor: '#f8f9fa' } : {}),
    ...(hasContent ? { color: '#000000', fontWeight: '500' } : {})
  };

  return (
    <Col md={colSize} className="mb-3">
      <FormGroup>
        <label className="form-control-label">{label}</label>
        <Input
          type={type as any}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={inputStyles}
        />
      </FormGroup>
    </Col>
  );
};

interface SectionProps {
  title: string;
  borderColor: string;
  children: React.ReactNode;
  backgroundColor?: string;
}

const Section: React.FC<SectionProps> = ({ 
  title, 
  borderColor, 
  children, 
  backgroundColor = '#f8f9fa' 
}) => (
  <div style={{
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px',
    backgroundColor
  }}>
    <h5 style={{
      marginBottom: '20px',
      color: '#495057',
      borderBottom: `2px solid ${borderColor}`,
      paddingBottom: '8px'
    }}>
      {title}
    </h5>
    {children}
  </div>
);

// ============ SEARCH COMPONENT ============
const ClientSearch: React.FC<ClientSearchProps> = ({ onClientFound, onError, token }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);

  const getLandlordData = useCallback(() => {
    if (!shouldSearch || !searchEmail.trim()) {
      return null;
    }
    return getLandlordByMailEndpoint(searchEmail, token);
  }, [searchEmail, token, shouldSearch]);

  const setLandlordInfo = useCallback(
    (landlordData: LandlordResponse) => {
      
      // Capturar el ID real de la respuesta de la API
      const clientInfo: ClientInfo = {
        landlord_id: landlordData.landlord_id || landlordData.id || `temp_client_${Date.now()}`,
        nombre: landlordData.name || '',
        email: landlordData.email || '',
        telefono: landlordData.phone || '',
        direccion: '' // VACÍO: Solo se llenará manualmente por el usuario
      };
      
      onClientFound(clientInfo);
      setShouldSearch(false);
      setSearchEmail('');
    },
    [onClientFound]
  );

  const handleSearchError = useCallback(
    (errorMessage: string) => {
      if (shouldSearch) {
        onError('Cliente no encontrado o error en la búsqueda');
      }
      setShouldSearch(false);
    },
    [onError, shouldSearch]
  );

  const [, , isSearching] = useAdminEntity<LandlordResponse>(
    getLandlordData,
    handleSearchError,
    setLandlordInfo
  );

  const handleSearch = useCallback(() => {
    if (!searchEmail.trim()) {
      onError('Por favor ingrese un email para buscar');
      return;
    }
    setShouldSearch(true);
  }, [searchEmail, onError]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchEmail(value);
    if (!value.trim() && shouldSearch) {
      setShouldSearch(false);
    }
  }, [shouldSearch]);

  // Determinar si el input de búsqueda tiene contenido
  const hasSearchContent = searchEmail.trim() !== '';

  return (
    <Row className="mb-4">
      <Col md="8">
        <FormGroup>
          <label className="form-control-label">Buscar cliente por email</label>
          <InputGroup>
            <Input
              type="email"
              placeholder="ejemplo@correo.com"
              value={searchEmail}
              onChange={handleEmailChange}
              onKeyPress={handleKeyPress}
              disabled={isSearching}
              style={{
                ...(hasSearchContent ? { color: '#000000', fontWeight: '500' } : {})
              }}
            />
            <InputGroupAddon addonType="append">
              <Button 
                color="success" 
                onClick={handleSearch}
                disabled={isSearching || !searchEmail.trim()}
              >
                {isSearching ? 'Buscando...' : 'Buscar'}
              </Button>
            </InputGroupAddon>
          </InputGroup>
          {isSearching && shouldSearch && (
            <small className="text-muted mt-1">
              Buscando cliente por email: {searchEmail}...
            </small>
          )}
        </FormGroup>
      </Col>
    </Row>
  );
};

// ============ MAIN COMPONENT ============
const ClientInformationQuotationComponent: React.FC<ClientInfoSectionProps> = ({ 
  clientInfo, 
  onClientChange,
  onClientFound,
  onError,
  token
}) => (
  <Section title="Información del Cliente" borderColor="#28a745">
    <ClientSearch
      onClientFound={onClientFound}
      onError={onError}
      token={token}
    />
    
    <Row>
      <Field
        label="Nombre completo"
        placeholder="Ingrese el nombre completo"
        value={clientInfo.nombre}
        onChange={(v) => onClientChange('nombre', v)}
        colSize="6"
      />
      <Field
        label="Email"
        type="email"
        placeholder="ejemplo@correo.com"
        value={clientInfo.email}
        onChange={(v) => onClientChange('email', v)}
        colSize="6"
      />
    </Row>
    <Row>
      <Field
        label="Teléfono"
        type="tel"
        placeholder="+57 300 123 4567"
        value={clientInfo.telefono}
        onChange={(v) => onClientChange('telefono', v)}
        colSize="6"
      />
      <Field
        label="Dirección"
        placeholder="Calle, carrera, número, ciudad"
        value={clientInfo.direccion}
        onChange={(v) => onClientChange('direccion', v)}
        colSize="6"
      />
    </Row>
  </Section>
);

export default ClientInformationQuotationComponent;